import React, { useCallback, useEffect, useState } from "react";
import { Languages, X } from "lucide-react";

const TARGET_LANGS = [
  { code: "en", label: "English" },
  { code: "ar", label: "العربية" },
  { code: "fr", label: "Français" },
  { code: "es", label: "Español" },
  { code: "pt", label: "Português" },
];

const defaultTarget = () => (localStorage.getItem("ao2_lang") || "en") === "en" ? "ar" : "en";

export default function SelectionTranslate() {
  const [box, setBox] = useState(null); // {x, y, text, target, result, loading}

  const fetchTranslation = useCallback(async (text, target) => {
    setBox((current) => (current && current.text === text ? { ...current, target, result: "", loading: true } : current));
    try {
      const res = await fetch(`https://translate.googleapis.com/translate_a/single?client=gtx&sl=auto&tl=${target}&dt=t&q=${encodeURIComponent(text)}`);
      if (!res.ok) throw new Error("Translation request failed");
      const data = await res.json();
      const result = (data[0] || []).map((segment) => segment[0]).join("");
      setBox((current) => (current && current.text === text ? { ...current, target, result, loading: false } : current));
    } catch {
      setBox((current) => (current && current.text === text ? { ...current, target, result: "Translation unavailable", loading: false } : current));
    }
  }, []);

  useEffect(() => {
    const onUp = (e) => {
      if (e.target.closest && e.target.closest("[data-sel-translate]")) return;
      if (e.target.closest && e.target.closest("input, textarea, select, [contenteditable='true']")) return;
      const selection = window.getSelection();
      const text = selection ? selection.toString().trim() : "";
      if (!text || text.length < 2 || text.length > 600) { setBox(null); return; }
      const range = selection.getRangeAt(0).getBoundingClientRect();
      const target = defaultTarget();
      setBox({ x: range.left + range.width / 2, y: range.top - 8, text, target, result: "", loading: true });
      fetchTranslation(text, target);
    };
    const onDown = (e) => { if (!(e.target.closest && e.target.closest("[data-sel-translate]"))) setBox(null); };
    const onKey = (e) => { if (e.key === "Escape") setBox(null); };
    document.addEventListener("mouseup", onUp);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mouseup", onUp);
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [fetchTranslation]);

  if (!box) return null;
  const left = Math.max(12, Math.min(box.x - 150, window.innerWidth - 312));
  const top = Math.max(12, box.y - 8);

  return (
    <div
      data-sel-translate
      translate="no"
      className="notranslate fixed z-[95] w-[300px] -translate-y-full rounded-xl glass border border-gold/30 p-4 shadow-2xl"
      style={{ left, top }}
      role="dialog"
      aria-label="Translate selected text"
      data-testid="selection-translate"
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <label htmlFor="selection-target" className="flex items-center gap-2 text-[0.6rem] uppercase tracking-widest text-gold font-body">
          <Languages size={13} /> Translate to
        </label>
        <button
          type="button"
          onClick={() => setBox(null)}
          className="text-silver/60 hover:text-gold transition-colors"
          aria-label="Close translation"
          data-testid="selection-translate-close"
        >
          <X size={15} />
        </button>
      </div>
      <select
        id="selection-target"
        value={box.target}
        onChange={(e) => fetchTranslation(box.text, e.target.value)}
        className="mb-3 w-full rounded-lg border border-gold/25 bg-black/70 px-3 py-2 text-xs font-body text-white outline-none focus:border-gold/60"
        data-testid="selection-translate-target"
      >
        {TARGET_LANGS.map((lang) => <option key={lang.code} value={lang.code}>{lang.label}</option>)}
      </select>
      {box.loading ? (
        <div className="flex gap-1.5 py-1" data-testid="selection-translate-loading">
          {[0, 1, 2].map((d) => <span key={d} className="h-1.5 w-1.5 rounded-full bg-gold animate-pulse" style={{ animationDelay: `${d * 0.15}s` }} />)}
        </div>
      ) : (
        <p className="text-sm font-body text-white leading-relaxed" dir={box.target === "ar" ? "rtl" : "ltr"} data-testid="selection-translate-result">{box.result}</p>
      )}
    </div>
  );
}
